import { useEffect, useState } from 'react';
import type React from 'react';

import { WebSocketEventTypeEnum } from '../../../enums/WebSocketEventTypeEnum';
import type File from '../../../interfaces/File';
import type { FileUpdate } from '../../../interfaces/File';
import type {
  WebSocketMessage,
  WebSocketMessageDataAiEvent,
} from '../../../interfaces/WebSocketMessage';
import { useAppStore } from '../../../store/store';
import { fileService } from '../services';

/**
 * React hook: Listens to file-related workspace WebSocket events and updates the local file list accordingly.
 *
 * - For "tag" events, fetches the detailed file with updated tags and patches the relevant file row.
 * - For other relevant events, collects affected file IDs and, using a timed interval, batches requests
 *   to refresh these rows (via `getSpecificFiles`) and trigger the `onFilesUpdated` callback.
 * - DocumentChat events are intentionally ignored.
 *
 * @param input.files           The current array of files in view.
 * @param input.setFiles        Setter to update the displayed files.
 * @param input.onFilesUpdated  Callback triggered when one or more files are updated in batch.
 */
export function useFilesWebSocket(input: {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  onFilesUpdated: (updatedFiles: FileUpdate[]) => void;
}) {
  const { files, setFiles, onFilesUpdated } = input;

  // Track file IDs that require batch updates from the server.
  const [pendingFileUpdates, setPendingFileUpdates] = useState<Set<number>>(new Set());

  useEffect(() => {
    /**
     * Handles a single workspace WebSocket event.
     * For file tag changes, only fetches the updated tags for the affected file.
     * For other relevant events (e.g., rename, move, etc.), enqueues the file for a batch update.
     */
    const handleWebSocketEvent = (event: WebSocketMessage) => {
      const payloadData = event?.data as { event_type?: string; file_id?: number } | undefined;
      const eventType = payloadData?.event_type;
      const fileId = payloadData?.file_id;
      const aiEventData = event?.data as WebSocketMessageDataAiEvent | undefined;

      // Ignore events not associated with a specific file.
      if (!fileId) return;

      // Explicitly ignore DocumentChat events as they're non-row-affecting.
      if (eventType === WebSocketEventTypeEnum.DocumentChat) return;

      // Only handle files currently displayed in the list.
      const fileIsInList = files.some((f) => f.id === fileId);

      // For Tags events, fetch only the updated tags for this file and merge.
      if (eventType === WebSocketEventTypeEnum.Tags && fileIsInList) {
        const aiStatus = aiEventData?.status?.trim().toLowerCase();
        if (aiStatus === 'queued' || aiStatus === 'classifying') {
          return;
        }
        fileService
          .getFile(fileId)
          .then((detail) => {
            setFiles((prevFiles) =>
              prevFiles.map((f) => (f.id !== fileId ? f : { ...f, tags: detail.tags })),
            );
          })
          .catch((err) => {
            // Error fetching file details for tag update; log for diagnostics.
            console.error('[useFiles] Error refreshing file after tags event:', err);
          });
        return;
      }

      // For other relevant events, enqueue the file for batched updates if present in list.
      if (fileIsInList) {
        setPendingFileUpdates((prev) => new Set(prev).add(fileId));
      }
    };

    // Subscribe to workspace WebSocket events using the global app store event bus.
    const unsubscribeWebSocketEvent = useAppStore
      .getState()
      .events.on.webSocketEvent(handleWebSocketEvent);

    // Cleanup the event subscription on unmount or when dependencies change.
    return unsubscribeWebSocketEvent;
  }, [files, setFiles]);

  useEffect(() => {
    // Establish a polling interval to process and batch fetch pending file updates.
    const interval = setInterval(async () => {
      if (pendingFileUpdates.size === 0) return;

      // Batch all currently pending file IDs to update.
      const fileIdsToUpdate = Array.from(pendingFileUpdates);

      // Reset the pending set before fetching (prevents race conditions if new IDs are added mid-fetch).
      setPendingFileUpdates(new Set());

      try {
        const response = await fileService.getSpecificFiles(fileIdsToUpdate);

        // Only proceed if the server returns updated files.
        if (response.myFiles && response.myFiles.length > 0) {
          onFilesUpdated(response.myFiles);
        }
      } catch (error) {
        // Log any error received while refreshing files from the server.
        console.error('Error updating files from WebSocket:', error);
      }
    }, 2000);

    // Cleanup: clear the polling interval when effect is disposed.
    return () => clearInterval(interval);
  }, [pendingFileUpdates, onFilesUpdated]);
}
